import { useMemo, useState } from 'react';
import {
  ArrowRight,
  Check,
  Copy,
  LoaderCircle,
  ScanLine,
  ShieldCheck,
} from 'lucide-react';
import { api } from '../lib/api';
import { trimPresets } from '../projectPresets';

type Project = {
  id: string;
  title: string;
  trimWidthIn?: number;
  trimHeightIn?: number;
  trimPresetId?: string;
  binding?: 'paperback' | 'hardcover';
  printMode?: 'black-white' | 'standard-color' | 'premium-color';
  targetPages: number;
};

type Preview = {
  source: {
    widthIn: number;
    heightIn: number;
    aspect: number;
  };
  target: {
    widthIn: number;
    heightIn: number;
    aspect: number;
  };
  pageCount: number;
  artPages: number;
  qrAssets: number;
  characterAssets: number;
  aspectChangePercent: number;
  isbnAction: string;
  warnings: string[];
};

export default function ProjectConverter({
  project,
  onClose,
  onConverted,
}: {
  project: Project;
  onClose: () => void;
  onConverted: (project: Project) => Promise<void> | void;
}) {
  const [title, setTitle] = useState(`${project.title} — Converted`);
  const [trimPresetId, setTrimPresetId] = useState('7x10');
  const [binding, setBinding] =
    useState<'paperback' | 'hardcover'>('paperback');
  const [printMode, setPrintMode] =
    useState<'black-white' | 'standard-color' | 'premium-color'>(
      'premium-color'
    );
  const [adaptMode, setAdaptMode] =
    useState<'smart-crop' | 'fit-whole'>('smart-crop');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState('');
  const [errorText, setErrorText] = useState('');

  const selectedTrim = useMemo(
    () => trimPresets.find(item => item.id === trimPresetId) || trimPresets[0],
    [trimPresetId]
  );

  const available = useMemo(
    () =>
      trimPresets.filter(item => {
        const bindingOk =
          binding === 'paperback' ? item.paperback : item.hardcover;
        const colorOk =
          printMode === 'black-white'
            ? item.blackWhite
            : printMode === 'standard-color'
              ? item.standardColor
              : item.premiumColor;
        return bindingOk && colorOk;
      }),
    [binding, printMode]
  );

  const getPreview = async () => {
    setBusy('preview');
    setErrorText('');
    try {
      const { data } = await api.post<Preview>(
        `/api/projects/${project.id}/convert/preview`,
        {
          trimPresetId,
          trimWidthIn: selectedTrim.widthIn,
          trimHeightIn: selectedTrim.heightIn,
          binding,
          printMode,
          adaptMode,
        }
      );
      setPreview(data);
    } catch {
      setErrorText('The conversion preview could not be created.');
    } finally {
      setBusy('');
    }
  };

  const convert = async () => {
    setBusy('convert');
    setErrorText('');
    try {
      const { data } = await api.post<Project>(
        `/api/projects/${project.id}/convert`,
        {
          title,
          trimPresetId,
          trimWidthIn: selectedTrim.widthIn,
          trimHeightIn: selectedTrim.heightIn,
          binding,
          printMode,
          adaptMode,
          copyAssets: true,
          clearIsbn: true,
        }
      );
      await onConverted(data);
    } catch {
      setErrorText('The project could not be converted.');
      setBusy('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/45 p-4 backdrop-blur-sm">
      <div className="max-h-[95vh] w-full max-w-6xl overflow-y-auto rounded-[30px] bg-[#fbfaf6] p-6 shadow-2xl sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="eyebrow text-[#ef5b3e]">CONVERT PROJECT</span>
            <h2 className="mt-2 text-3xl font-black tracking-[-0.04em]">
              Duplicate the book. Change only the format.
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-black/50">
              Story, characters, art, lettering and commercial content are copied
              unchanged. The new project receives its own print setup and
              non-destructive page-fit instructions.
            </p>
          </div>
          <button onClick={onClose} className="iconButton" aria-label="Close">
            ×
          </button>
        </div>

        <div className="mt-7 grid gap-6 lg:grid-cols-[1fr_390px]">
          <section className="card p-6">
            <Field label="Converted project title">
              <input
                value={title}
                onChange={event => setTitle(event.target.value)}
              />
            </Field>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Field label="Binding">
                <select
                  value={binding}
                  onChange={event => {
                    const value = event.target.value as
                      | 'paperback'
                      | 'hardcover';
                    setBinding(value);
                    if (value === 'hardcover' && printMode === 'standard-color')
                      setPrintMode('premium-color');
                    setPreview(null);
                  }}
                >
                  <option value="paperback">Paperback</option>
                  <option value="hardcover">Hardcover</option>
                </select>
              </Field>

              <Field label="Print mode">
                <select
                  value={printMode}
                  onChange={event => {
                    setPrintMode(
                      event.target.value as
                        | 'black-white'
                        | 'standard-color'
                        | 'premium-color'
                    );
                    setPreview(null);
                  }}
                >
                  <option value="black-white">Black & white</option>
                  <option value="standard-color">Standard color</option>
                  <option value="premium-color">Premium color</option>
                </select>
              </Field>
            </div>

            <div className="mt-5">
              <div className="mb-2 text-xs font-black uppercase tracking-[.12em] text-black/45">
                Target dimensions
              </div>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {available.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setTrimPresetId(item.id);
                      setPreview(null);
                    }}
                    className={`rounded-2xl border p-4 text-left ${
                      trimPresetId === item.id
                        ? 'border-[#ef5b3e] bg-[#fff0eb]'
                        : 'border-black/8 bg-white'
                    }`}
                  >
                    <div className="flex justify-between gap-2">
                      <b>{item.label}</b>
                      {trimPresetId === item.id && (
                        <Check size={16} className="text-[#ef5b3e]" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <div className="mb-2 text-xs font-black uppercase tracking-[.12em] text-black/45">
                Art adaptation
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => {
                    setAdaptMode('smart-crop');
                    setPreview(null);
                  }}
                  className={`rounded-2xl border p-4 text-left ${
                    adaptMode === 'smart-crop'
                      ? 'border-[#ef5b3e] bg-[#fff0eb]'
                      : 'border-black/8 bg-white'
                  }`}
                >
                  <b>Smart crop / fill</b>
                  <p className="mt-2 text-sm text-black/45">
                    Fill the new page ratio and crop only what falls outside it.
                    Original art files stay untouched.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setAdaptMode('fit-whole');
                    setPreview(null);
                  }}
                  className={`rounded-2xl border p-4 text-left ${
                    adaptMode === 'fit-whole'
                      ? 'border-[#ef5b3e] bg-[#fff0eb]'
                      : 'border-black/8 bg-white'
                  }`}
                >
                  <b>Fit whole artwork</b>
                  <p className="mt-2 text-sm text-black/45">
                    Keep every pixel of the source art and add page-space where
                    ratios differ.
                  </p>
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={getPreview}
              disabled={busy === 'preview'}
              className="secondary mt-6"
            >
              {busy === 'preview' ? (
                <LoaderCircle size={17} className="animate-spin" />
              ) : (
                <ScanLine size={17} />
              )}
              {busy === 'preview' ? 'Analyzing…' : 'Preview conversion'}
            </button>
          </section>

          <aside className="card p-5 h-fit">
            {!preview ? (
              <div className="py-10 text-center">
                <Copy className="mx-auto text-black/25" size={34} />
                <div className="mt-4 font-black">Source assets stay intact</div>
                <p className="mt-2 text-sm leading-relaxed text-black/45">
                  Run the preview to compare the source and target aspect ratios.
                </p>
              </div>
            ) : (
              <>
                <span className="eyebrow text-[#ef5b3e]">CONVERSION PREVIEW</span>

                <div className="mt-5 flex items-center justify-center gap-4">
                  <PageShape
                    width={preview.source.widthIn}
                    height={preview.source.heightIn}
                    label={`${preview.source.widthIn} × ${preview.source.heightIn}`}
                  />
                  <ArrowRight className="shrink-0 text-black/30" />
                  <PageShape
                    width={preview.target.widthIn}
                    height={preview.target.heightIn}
                    label={`${preview.target.widthIn} × ${preview.target.heightIn}`}
                  />
                </div>

                <div className="mt-6 space-y-2 text-sm">
                  <Summary label="Pages" value={String(preview.pageCount)} />
                  <Summary
                    label="Artwork copied"
                    value={`${preview.artPages} pages`}
                  />
                  <Summary
                    label="Character assets"
                    value={String(preview.characterAssets)}
                  />
                  <Summary
                    label="QR assets"
                    value={String(preview.qrAssets)}
                  />
                  <Summary
                    label="Aspect change"
                    value={`${preview.aspectChangePercent.toFixed(1)}%`}
                  />
                </div>

                <div className="mt-5 rounded-2xl bg-amber-50 p-4 text-xs leading-relaxed text-amber-900">
                  <b>ISBN:</b> {preview.isbnAction}
                </div>

                {preview.warnings.map(warning => (
                  <div
                    key={warning}
                    className="mt-2 rounded-xl bg-[#f1eee6] p-3 text-xs text-black/55"
                  >
                    {warning}
                  </div>
                ))}

                <button
                  type="button"
                  onClick={convert}
                  disabled={busy === 'convert'}
                  className="primary mt-6 w-full"
                >
                  {busy === 'convert' ? (
                    <LoaderCircle size={17} className="animate-spin" />
                  ) : (
                    <Copy size={17} />
                  )}
                  {busy === 'convert'
                    ? 'Duplicating assets…'
                    : 'Create converted project'}
                </button>

                <div className="mt-4 flex items-start gap-2 text-xs text-black/40">
                  <ShieldCheck size={15} className="mt-0.5 shrink-0" />
                  The source project is never modified.
                </div>
              </>
            )}
          </aside>
        </div>

        {errorText && (
          <div className="mt-5 rounded-2xl border border-[#ef5b3e]/20 bg-[#fff1ec] p-4 text-sm text-[#8d3526]">
            {errorText}
          </div>
        )}
      </div>
    </div>
  );
}

function PageShape({
  width,
  height,
  label,
}: {
  width: number;
  height: number;
  label: string;
}) {
  const ratio = width / height;
  return (
    <div className="text-center">
      <div
        className="mx-auto grid place-items-center border-2 border-black/15 bg-white shadow"
        style={{
          width: `${Math.max(55, ratio * 105)}px`,
          height: '105px',
        }}
      >
        <span className="text-[9px] font-black text-black/35">{label}</span>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-black uppercase tracking-[.12em] text-black/45">
        {label}
      </span>
      {children}
    </label>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-black/6 pb-2">
      <span className="text-black/40">{label}</span>
      <b className="text-right">{value}</b>
    </div>
  );
}
